import { MutationCache, QueryClient } from '@tanstack/react-query'
import { isRateLimited } from '@/lib/httpError'

/**
 * The app's TanStack Query client.
 *
 * A factory rather than a module-level instance so the rules below can be
 * unit-tested — `vitest.config.ts` only picks up `.test.ts` under `src`, in a
 * node environment, so anything worth asserting has to live outside the
 * provider component. `QueryProvider` builds the one the app uses.
 */

/** The audit trail's query key, named here so the cache-wide rule can use it. */
export const AUDIT_TRAIL_KEY = ['audit-trail'] as const

/**
 * Root key of the procedures list's per-row progress journeys
 * (`useProcedureJourneys`), named here for the same reason.
 */
export const PROCEDURE_JOURNEY_KEY = ['procedure-journey'] as const

/**
 * Mark every cached list-progress journey stale WITHOUT refetching it.
 *
 * A journey is a consequence of any write, like the audit trail. `refetchType:
 * 'none'` is the point: a write on the procedures list itself must not re-fire
 * one request per visible row. Nothing needs the new progress until the list is
 * next opened, and then it refetches, exactly as the list did when this cache
 * was component state.
 */
export function markProcedureJourneysStale(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: PROCEDURE_JOURNEY_KEY, refetchType: 'none' })
}

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    /**
     * Every successful mutation refreshes the audit trail. One rule,
     * cache-wide.
     *
     * The trail is the one view in the app that is a consequence of EVERY
     * other action — placing a product in storage, assigning a step,
     * correcting a volume, printing a label. Asking each of those mutations to
     * remember to invalidate it does not work: the storage-location mutation
     * did not, so a placement only appeared after a manual refresh, and every
     * mutation added later would have been one more chance to forget.
     *
     * So the rule lives at the cache, which is the only place it cannot be
     * forgotten. It is cheap: the trail query is only enabled while a panel
     * showing it is open, so for every other mutation this invalidates
     * nothing.
     *
     * Writes that do NOT go through a mutation — `formsApi.saveFieldValue` is
     * the one that matters — invalidate it themselves; see
     * `useAuditTrailInvalidator`.
     *
     * The procedures list's progress journeys ride the same rule, marked stale
     * only (see `markProcedureJourneysStale`).
     */
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        // Skip the trail's own writes, if it ever gets any: re-invalidating
        // the key a mutation just settled would loop.
        if (mutation.options.mutationKey?.[0] === AUDIT_TRAIL_KEY[0]) return
        void client.invalidateQueries({ queryKey: AUDIT_TRAIL_KEY })
        markProcedureJourneysStale(client)
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        /**
         * One retry — except against the rate limiter (z8nrz7cnhz).
         *
         * A flat `retry: 1` meant a burst that tripped the API's per-user,
         * per-endpoint ceiling (120/min then, 600/min since z8nrz7e1c2)
         * immediately doubled itself: every refused request asked
         * again, and the second ask was refused too. Retrying a 429 cannot
         * succeed while the window is still open — it only spends the
         * allowance the page needs once that window resets, which is how one
         * case page turned a brief overage into a sustained one.
         */
        retry: (failureCount, error) => !isRateLimited(error) && failureCount < 1,
        refetchOnWindowFocus: false,
      },
    },
  })
  return client
}
